<template>
  <SuccessfulActionModal
    :bulkImport="true"
    v-if="updateMessage.successful.show"
    @close="cancelVendorImport"
  >
  </SuccessfulActionModal>

  <template v-if="isSaving">
    <LoadingGiftIcon></LoadingGiftIcon>
  </template>
  <template v-if="!isSaving && !updateMessage.successful.show">
    <div class="container-fluid">
      <div class="row">
        <div class="col mb-3">
          <div
            :class="updateMessage.failed.class"
            v-if="updateMessage.failed.show"
          >
            <p class="mb-0 fw-bold fs-4 text-white">
              {{ updateMessage.failed.text }}
            </p>
          </div>
        </div>

        <div
          class="d-flex align-items-end justify-content-between text-start my-3 my-lg-5"
        >
          <h1 class="mb-0">Import Vendors</h1>
          <div class="d-flex align-items-center justify-content-center gap-2">
            <p class="text-danger mb-0" v-if="!performChecksOnVendors">
              Complete required sections before importing data.
            </p>
            <template v-if="this.currentPage !== 1">
              <div class="page-item">
                <a
                  class="page-link"
                  @click="previousPage()"
                  aria-label="Previous"
                  ><b-icon-chevron-left></b-icon-chevron-left
                ></a>
              </div>
            </template>
            <template v-if="currentVendor"
              >
              <div class="dropdown">
                <button
                  class="form-select text-start text-truncate"
                  style="max-width: 15rem; min-width: 15rem"
                  type="button"
                  data-bs-toggle="dropdown"
                >
                  {{ currentVendor.name }}
                </button>
                <ul class="dropdown-menu">
                  <li
                    class="dropdown-item"
                    v-for="vendor in getImportedVendors"
                    :key="vendor._id"
                    @click="selectVendor(vendor)"
                  >
                    <template
                      v-if="
                        vendor._id ===
                        (currentVendor ? currentVendor._id : null)
                      "
                      ><span class="text-primary fw-bold">{{
                        vendor.name
                      }}</span></template
                    >
                    <template v-else> {{ vendor.name }}</template>
                  </li>
                </ul>
              </div>
            </template>
            <template v-else>
              <li class="page-item active">
                <a class="page-link" href="#">{{ currentPage }}</a>
              </li>
            </template>
            <template v-if="!this.isLastPage">
              <div class="page-item">
                <a class="page-link" @click="nextPage()" aria-label="Next"
                  ><b-icon-chevron-right></b-icon-chevron-right
                ></a>
              </div>
            </template>

            <div class="d-flex gap-2">
              <button
                class="btn btn-sm btn-secondary"
                @click="cancelVendorImport()"
                aria-label="Previous"
              >
                Cancel
              </button>
              <button
                class="btn btn-sm btn-success"
                :class="{ disabled: !this.performChecksOnVendors }"
                @click.stop="handleCreatingVendorsFromCsvUpload()"
              >
                Import
                <span class="fw-bold">{{ getImportedVendors.length }}</span>
                vendors
              </button>
            </div>
          </div>

          <!-- End Col -->
        </div>
      </div>
    </div>
    <ImportedVendorListItem
      v-for="vendor in paginatedData"
      :key="vendor._id"
      :vendor="vendor"
      @setVendor="setVendor"
    ></ImportedVendorListItem>
  </template>
</template>

<script>
import ImportedVendorListItem from "./ImportedVendorListItem.vue";
import LoadingGiftIcon from "../../components/ui/LoadingGiftIcon.vue";
import SuccessfulActionModal from "../../components/ui/modals/success/SuccessfulActionModal.vue";

export default {
  name: "ImportedVendorList",
  components: {
    ImportedVendorListItem,
    LoadingGiftIcon,
    SuccessfulActionModal,
  },
  created() {
    this.$store.dispatch("brands/deactivateQuickEdit");
  },
  data() {
    return {
      currentVendor: null,
      updateMessage: {
        successful: {
          show: false,
        },
        failed: {
          text: "Something went wrong while attempting to update your vendor. Please try again.",
          class:
            "d-flex p-3 justify-content-center align-items-center bg-danger",
          show: false,
        },
      },
      itemsPerPage: 1,
      currentPage: 1,
      originalData: {}, // Update data that the user modifies
      isSaving: false,
    };
  },
  computed: {
    isLastPage() {
      return (
        this.getImportedVendors.length <= this.itemsPerPage ||
        this.currentPage === Math.ceil(this.getImportedVendors.length)
      );
    },
    isPreviousButtonDisabled() {
      return this.currentPage === 1;
    },
    isNextButtonDisabled() {
      return this.currentPage === this.totalPages;
    },
    isFilterActive() {
      return this.selectedCategoryValue ? true : false;
    },
    getImportedVendors() {
      return this.$store.getters["brands/getImportedVendors"];
    },
    totalPages() {
      return Math.ceil(this.getImportedVendors.length / this.itemsPerPage);
    },
    paginatedData() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      const vendors = this.$store.getters["brands/getImportedVendors"];
      // console.log("next vendor", vendors);
      return vendors.slice(startIndex, endIndex);
    },
    performChecksOnVendors() {
      const vendorsArray = this.$store.getters["brands/getImportedVendors"];
      for (const vendor of vendorsArray) {
        const addressSectionValid =
          (vendor.address.city !== "" &&
            vendor.address.state !== "" &&
            vendor.address.country !== "") ||
          (vendor.address.city === "" &&
            vendor.address.state === "" &&
            vendor.address.country === "");

        const generalSectionValid =
          vendor.name !== "" &&
          Array.isArray(vendor.categories) &&
          vendor.categories.length > 0 &&
          vendor.description !== "";

        if (!addressSectionValid || !generalSectionValid) {
          // console.log("addressSectionValid", addressSectionValid);
          // console.log("generalSectionValid", generalSectionValid);
          return false;
        }
      }
      return true;
    },
  },
  methods: {
    async createSingleVendorAddress(addressData) {
      try {
        // Get the Bold client
        const api = await this.getBoldClient();

        // Prepare address lines
        const addressLines = [];
        if (addressData.lines[0]) {
          addressLines.push(addressData.lines[0]);
        }
        if (addressData.lines[1]) {
          addressLines.push(addressData.lines[1]);
        }

        // Create an address object
        let newAddress = {
          city: addressData.city,
          state: addressData.state,
          postalCode: addressData.postalCode,
          country: addressData.country,
        };

        // Conditionally add the 'lines' property
        if (addressLines.length > 0) {
          newAddress.lines = addressLines;
        }

        // Create the address using the API
        const response = await api.createAddresses(null, [newAddress]);

        // Handle a successful response
        this.isSuccessful = true;
        // console.log("address created", response);

        return response.data.result.records[0]._id;
      } catch (error) {
        console.log("Error creating address", error);
        // Handle the error as needed
        console.log("address was NOT created");
      }
    },
    async createVendor(vendors) {
      try {
        const api = await this.getBoldClient();
        const response = await api.createVendors(null, vendors);
        // console.log("vendor response", response);
        // const vendorIds = response.data.result.records.map(
        //   (vendor) => vendor._id
        // );
        // console.log("vendorIds", vendorIds);

        return response;
      } catch (error) {
        // Handle error
        console.log("Error creating vendor:", error);
      }
    },
    async handleCreatingVendorsFromCsvUpload() {
      this.isSaving = true;
      try {
        const vendorsData = this.$store.getters["brands/getImportedVendors"];
        const updatedVendorsData = [];
        console.log("vendorsData", vendorsData);

        for (let i = 0; i < vendorsData.length; i++) {
          const vendor = vendorsData[i];
          if (
            vendor.address.city &&
            vendor.address.state &&
            vendor.address.country
          ) {
            const addressId = await this.createSingleVendorAddress(
              vendor.address
            );
            vendor.address = addressId.toString();
            const { _id, ...vendorToCreate } = vendor;
            updatedVendorsData.push(vendorToCreate);
          } else {
            delete vendorsData[i].address; // Remove the address property if condition is not met
            delete vendor._id;
          }
        }
        // console.log("updatedVendorsData", updatedVendorsData);

        await this.createVendor(updatedVendorsData);

        console.log("Vendors and addresses created successfully");
        this.handleSuccess();
      } catch (error) {
        console.error("Error creating vendors and addresses:", error);
        this.handleError();
      }
    },
    async loadCreatedVendors(vendorIds) {
      const api = await this.getBoldClient();

      let filterParam = {
        _id: { $in: vendorIds },
      };

      // let selection = "metadata name categories address contact";

      // console.log("filterParam", filterParam);

      const queryParameters = {
        sort: "name",
        expand: "media address",
      };

      try {
        // Fetch kits using the constructed filterParam
        const response = await api.listVendors(queryParameters, {
          filter: filterParam,
          // selection: selection,
        });

        const vendors = response.data.result.records;
        // console.log("vendors", vendors);

        this.$store.commit("brands/SET_IMPORTED_VENDORS", vendors);
      } catch (error) {
        // Handle errors
        this.vendorsAreLoading = false;
        console.log("vendors did not load", error);
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    selectVendor(vendor) {
      const index = this.getImportedVendors.findIndex(
        (v) => v._id === vendor._id
      );
      if (index !== -1) {
        this.currentVendor = this.getImportedVendors[index];
        // Optionally, update currentPage based on the selected vendor's position in the list
        this.currentPage = Math.floor(index / this.itemsPerPage) + 1;
      }
    },
    setVendor(vendor) {
      this.currentVendor = vendor;
    },
    activateQuickEdit() {
      // Trigger mutation or action to set quickEditActive to true
      this.$store.dispatch("brands/activateQuickEdit");
    },
    deactivateQuickEdit() {
      // Trigger mutation or action to set quickEditActive to false
      this.$store.dispatch("brands/deactivateQuickEdit");
    },
    showConfirmDeleteModal() {
      this.isConfirmDeleteModalVisible = true;
    },
    closeConfirmDeleteModal() {
      this.isConfirmDeleteModalVisible = false;
    },
    handleSuccess() {
      this.isSaving = false;
      this.updateMessage.successful.show = true;
      // setTimeout(() => {
      //   this.updateMessage.successful.show = false;
      //   this.cancelVendorImport();
      //   window.location.reload();
      // }, 4000);
    },
    handleError() {
      this.isSaving = false;
      this.updateMessage.failed.show = true;
      setTimeout(() => {
        this.updateMessage.failed.show = false;
      }, 5000);
    },
    toggleBulkImportModal() {
      this.showBulkImportModal = !this.showBulkImportModal;
    },
    cancelVendorImport() {
      this.$router.push({ path: "/vendors" });
    },
  },
};
</script>

<style scoped>
th,
a {
  cursor: pointer;
}

.dropdown-menu {
  max-height: 280px;
  overflow-y: auto;
}
</style>
